import { useState } from "react";
import { Deeplink } from "../../models/interfaces/deeplink";
import {
    CheckCircleIcon,
    ClipboardIcon,
} from '@heroicons/react/outline';
import { Modal } from "../modal/modal";
import './deeplink-search-hits.styles.scss';
import { UserTag } from "../../models/interfaces/user-tag";
import LinkInfo from "../lead-links/lead-link-view";
import { useTranslation } from 'react-i18next';
import { SymmioAccessLinkTypeLabels } from "../../models/symmio-access-types";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { createPortal } from 'react-dom';
import { createLogger } from "../../utilities/logger";

const log = createLogger("DeeplinksSearchHits");

interface DeeplinksSearchHitsProps {
    deeplinks: Deeplink[];
    editClickHandler: (deeplink: Deeplink) => void;
    duplicateClickHandler: (deeplink: Deeplink) => Promise<void>;
    deleteClickHandler: (deeplink: Deeplink) => void;
}

const DeeplinksSearchHits: React.FC<DeeplinksSearchHitsProps> = (props) => {
    const { deeplinks, editClickHandler, duplicateClickHandler, deleteClickHandler } = props;
    const [openQRModal, setOpenQRModal] = useState(false);
    const [copiedLinkId, setCopiedLinkId] = useState('');
    const [modalDeeplink, setModalDeeplink] = useState<Deeplink | undefined>();
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [menuRowId, setMenuRowId] = useState<string | null>(null);
    const { t } = useTranslation();

    const handleQRClick = (deeplink: Deeplink) => {
        setModalDeeplink(deeplink);
        setOpenQRModal(true);
    }

    const handleLinkCopy = (deeplink: Deeplink | undefined) => {

        if (!deeplink?.url || !deeplink?.id) {
            log.error('Copy link failed. No id or URL available', {
                link: deeplink,
                missing: { id: !deeplink?.id, url: !deeplink?.url },
            });
            return;
        }
        // TODO : Global exception handling
        // https://app.clickup.com/t/2219993/FMS-1236

        navigator.clipboard.writeText(deeplink.url);
        setCopiedLinkId(deeplink.id);
        setTimeout(() => {
            setCopiedLinkId('');
        }, 2000);
    }

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, deeplink: Deeplink) => {
        event.stopPropagation();
        setMenuAnchor(event.currentTarget);
        setMenuRowId(deeplink.id ?? null);
    }

    const getMenuPosition = () => {
        if (!menuAnchor) return { top: 0, left: 0 };
        const rect = menuAnchor.getBoundingClientRect();
        return {
            top: rect.bottom + window.scrollY + 4,
            left: rect.right + window.scrollX - 140,
        };
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setMenuRowId(null);
    }

    const handleMenuAction = (action: 'edit' | 'duplicate' | 'delete') => {
        const deeplink = deeplinks.find(d => d.id === menuRowId);
        if (!deeplink) return;
        handleMenuClose();
        if (action === 'edit') {
            editClickHandler(deeplink);
        } else if (action === 'duplicate') {
            duplicateClickHandler(deeplink);
        } else if (action === 'delete') {
            deleteClickHandler(deeplink);
        }
    }


    function tagsToMarkup(tags: UserTag[]) {
        let tagArray: string[] = [];

        for (var i = 0; i < tags.length; i++) {
            tagArray.push(tags[i].name);
        }

        let tagString = tagArray.join(", ");

        return (
            <>
                {tagString}
            </>
        );
    }

    return deeplinks.length ? (
        <>
            {deeplinks.map((deeplink, index) => {
                const usersCreated = deeplink.used ?? 0;
                const className = (deeplink.status === 'active') ? '' : 'disabled';
                const linkLimit = deeplink.limit ?? "unlimited";

                return (
                    <tr className={`${className} table-row-container`} onClick={() => { handleQRClick(deeplink) }} key={`deeplink_${index}`}>
                        <td>
                            <div className={'icon-container'} onClick={(event) => {
                                event.stopPropagation();
                                handleLinkCopy(deeplink);
                            }}>
                                {(copiedLinkId === deeplink.id) ?
                                    <CheckCircleIcon className="h-7 w-7 my-1.5 mx-1.5" />
                                    : <ClipboardIcon className="h-7 w-7 my-1.5 mx-1.5" />
                                }
                            </div>
                        </td>
                        <td><strong>{deeplink.name}</strong></td>
                        <td><strong>{deeplink.linkType ? SymmioAccessLinkTypeLabels[deeplink.linkType] : ""}</strong></td>
                        <td><strong className="show-on-mobile mr-1">{t('deeplinks.edit.group')}</strong>{deeplink.groupName}</td>
                        <td>{// if
                            (deeplink.tags && deeplink.tags.length > 0) &&
                            <>
                                <strong className="show-on-mobile mr-1">{t('deeplinks.edit.tags')}</strong>
                                {tagsToMarkup(deeplink.tags)}
                            </>
                        }</td>
                        <td><strong className="show-on-mobile mr-1">{t('deeplinks.edit.usedLimit')}</strong>{`${usersCreated}/${linkLimit}`}</td>
                        <td>
                            <ClickAwayListener onClickAway={handleMenuClose}>
                                <div className='action-menu-container'>
                                    <div
                                        className='action-menu-trigger'
                                        onClick={(event) => handleMenuOpen(event, deeplink)}
                                    >
                                        <MoreVertIcon />
                                    </div>
                                    {menuAnchor !== null && menuRowId === deeplink.id &&
                                        createPortal(
                                            <div
                                                className='action-menu'
                                                style={{ position: 'absolute', top: getMenuPosition().top, left: getMenuPosition().left }}
                                            >
                                                <div className='action-menu-item' onClick={() => handleMenuAction('edit')}>
                                                    {t('buttons.btn_edit')}
                                                </div>
                                                <div className='action-menu-item' onClick={() => handleMenuAction('duplicate')}>
                                                    {t('buttons.btn_duplicate')}
                                                </div>
                                                <div className='action-menu-item action-menu-item--danger' onClick={() => handleMenuAction('delete')}>
                                                    {t('buttons.btn_delete')}
                                                </div>
                                            </div>,
                                            document.body
                                        )
                                    }
                                </div>
                            </ClickAwayListener>
                        </td>
                    </tr>
                )
            })}
            <script>
                <Modal
                    isOpen={openQRModal}
                    isLoading={false}
                    onClose={() => { setOpenQRModal(false) }}
                    title={t('deeplinks.qr_modal.title')}>
                    <LinkInfo linkData={modalDeeplink}></LinkInfo>
                </Modal>
            </script>
        </>
    ) : (
        <td className={`pt-6`} colSpan={8}>
            <div className="flex justify-center items-center h-full">
                <span className="text-center">
                    {t('deeplinks.noLinks')}
                </span>
            </div>
        </td>
    )
}

export default DeeplinksSearchHits;
